/**
 * Demo Application
 * For testing the DiagramEditor library
 */

import { useState, useEffect, useRef } from 'react'
import DiagramEditorMultiCanvas from '../lib/DiagramEditorMultiCanvas'
import {
  DiagramData,
  importFromJSON,
  MenuBar,
  NewDiagramModal,
  NewDiagramConfig,
  ExportOptionsModal,
  JSONImportModal,
  exportForAI,
  exportFull,
  createEditTracker,
  trackNodeMove,
  trackNodeAdd,
  trackNodeDelete,
  trackNodeStyle,
  type EditTracker,
  type ExportOptions,
} from '../lib'
import { useKeyboardShortcuts, CommonShortcuts } from '../lib/hooks/useKeyboardShortcuts'
import { useUnsavedChanges } from '../lib/hooks/useUnsavedChanges'
import { ModalProvider, useModal } from '../lib/components/modals/ModalProvider'
import JSONImportSidebar from './components/JSONImportSidebar'
import DemoGallery from './components/DemoGallery'
import Dashboard from './components/Dashboard'
import { DEMO_EXAMPLES, type DemoExample } from './examples'
import {
  loadProjects,
  createProject,
  renameProject as renameProjectInStore,
  deleteProject as deleteProjectInStore,
  touchProject,
  canvasKeyFor,
  type Project,
} from './projects'
import '../lib/styles/menubar.css'
import './App.css'

// A fresh empty canvas of a given diagram type (used to seed a new project).
const blankOfType = (diagramType: string): DiagramData => ({
  diagramType: diagramType as DiagramData['diagramType'],
  title: 'Untitled Diagram',
  description: '',
  nodes: [],
  edges: [],
  containers: [],
  layout: { algorithm: 'dagre', direction: diagramType === 'architecture' ? 'LR' : 'TB' },
})

// UI State Management (separate from diagram data)
interface UIState {
  showNodePalette: boolean
  showPropertiesPanel: boolean
}

// Load UI state from localStorage
const loadUIState = (): UIState => {
  try {
    const saved = localStorage.getItem('diagram-editor-ui-state')
    if (saved) {
      return JSON.parse(saved)
    }
  } catch (error) {
    console.error('Failed to load UI state:', error)
  }
  // Default state
  return {
    showNodePalette: true,
    showPropertiesPanel: true,
  }
}

// Save UI state to localStorage
const saveUIState = (state: UIState) => {
  try {
    localStorage.setItem('diagram-editor-ui-state', JSON.stringify(state))
  } catch (error) {
    console.error('Failed to save UI state:', error)
  }
}

// Example diagram data
const exampleDiagram: DiagramData = {
  diagramType: 'architecture',
  title: 'E-Commerce System',
  description: 'Example architecture diagram',
  containers: [
    {
      id: 'frontend',
      label: 'Frontend Layer',
      type: 'layer',
      position: { x: 50, y: 50 },
      size: { width: 300, height: 200 },
      style: {
        bgColor: '#e3f2fd',
        borderColor: '#90caf9',
      },
      icon: '🌐',
    },
    {
      id: 'backend',
      label: 'Backend Services',
      type: 'layer',
      position: { x: 400, y: 50 },
      size: { width: 300, height: 200 },
      style: {
        bgColor: '#f3e5f5',
        borderColor: '#ce93d8',
      },
      icon: '⚙️',
    },
  ],
  nodes: [
    {
      id: 'react-app',
      label: 'React App',
      nodeType: 'component',
      parentContainer: 'frontend',
      position: { x: 100, y: 120 },
      icon: '⚛️',
      style: {
        bgColor: '#e3f2fd',
        borderColor: '#90caf9',
      },
    },
    {
      id: 'api-gateway',
      label: 'API Gateway',
      nodeType: 'api',
      parentContainer: 'backend',
      position: { x: 450, y: 120 },
      icon: '🚪',
      style: {
        bgColor: '#f3e5f5',
        borderColor: '#ce93d8',
      },
    },
    {
      id: 'database',
      label: 'PostgreSQL',
      nodeType: 'database',
      position: { x: 450, y: 300 },
      icon: '🗄️',
      style: {
        bgColor: '#e8f5e9',
        borderColor: '#81c784',
      },
    },
  ],
  edges: [
    {
      source: 'react-app',
      target: 'api-gateway',
      label: 'API Calls',
      edgeType: 'http',
      protocol: 'REST',
      type: 'smoothstep',
    },
    {
      source: 'api-gateway',
      target: 'database',
      label: 'SQL Queries',
      edgeType: 'data-flow',
      type: 'smoothstep',
    },
  ],
  layout: {
    algorithm: 'dagre',
    direction: 'LR',
  },
}

// Read the optional collaboration room from the URL once at module load.
// ?room=<roomId> joins a live y-websocket room; ?sync=<wsUrl> (or VITE_SYNC_URL)
// overrides the default server. When no room param is present the demo renders
// exactly as before.
const collabParams = (() => {
  if (typeof window === 'undefined') return null
  const params = new URLSearchParams(window.location.search)
  const room = params.get('room')
  if (!room) return null
  const syncUrl =
    params.get('sync') || (import.meta as any).env?.VITE_SYNC_URL || 'ws://localhost:1234'
  return { url: syncUrl, room }
})()

function AppInner() {
  const modal = useModal()
  const [diagramData, setDiagramData] = useState<DiagramData | null>(exampleDiagram)
  const [showJsonEditor, setShowJsonEditor] = useState(false)
  const [error, setError] = useState<string>('')
  // Removed activeTab - Icon Gallery moved to Node Palette
  const [showNewDiagramModal, setShowNewDiagramModal] = useState(false)
  const [showGrid, setShowGrid] = useState(true)
  const [isExportModalOpen, setIsExportModalOpen] = useState(false)
  // Force reload counter - increments when JSON is explicitly imported
  const [importCounter, setImportCounter] = useState(0)
  const [isJSONImportModalOpen, setIsJSONImportModalOpen] = useState(false)

  // Marketing landing (home) → projects dashboard → editor. A collab room (?room=)
  // jumps straight to the editor; otherwise the user lands on home.
  const [view, setView] = useState<'home' | 'dashboard' | 'editor'>(collabParams ? 'editor' : 'home')
  // Figma-style projects: the user's persistent workspaces.
  const [projects, setProjects] = useState<Project[]>(() => (collabParams ? [] : loadProjects()))
  const [activeProjectId, setActiveProjectId] = useState<string | null>(null)
  // Per-project canvas persistence key (undefined => ephemeral example/preview).
  const [persistenceKey, setPersistenceKey] = useState<string | undefined>(undefined)

  // UI State (independent of diagram data)
  const [uiState, setUIState] = useState<UIState>(loadUIState())

  // File input ref for opening JSON
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Multi-canvas addTab function reference
  const addTabRef = useRef<((name?: string, data?: DiagramData) => string) | null>(null)

  // Edit tracking for AI export
  const editTrackerRef = useRef<EditTracker>(createEditTracker())
  const previousDataRef = useRef<DiagramData | null>(null)
  const initialNodesRef = useRef<Set<string>>(new Set())

  // Unsaved changes tracking
  const { hasUnsavedChanges, markAsSaved, markAsUnsaved, warnBeforeLeaving, resetTracking } =
    useUnsavedChanges({
      warnOnBrowserClose: true,
      confirmCallback: (message) => modal.confirm({ message, type: 'warning' }),
    })

  // Save UI state to localStorage whenever it changes
  useEffect(() => {
    saveUIState(uiState)
  }, [uiState])

  // Initialize edit tracking on mount
  useEffect(() => {
    if (diagramData) {
      initialNodesRef.current = new Set(diagramData.nodes.map(n => n.id))
      previousDataRef.current = diagramData
    }
  }, []) // Run only on mount

  // Keep the in-app view in sync with the browser Back/Forward buttons.
  useEffect(() => {
    const onPop = (e: PopStateEvent) => {
      const v = (e.state as { gv2View?: string } | null)?.gv2View
      if (v === 'editor') {
        setView('editor')
      } else if (v === 'dashboard') {
        setProjects(loadProjects())
        setView('dashboard')
      } else {
        setProjects(loadProjects())
        setView(collabParams ? 'editor' : 'home')
      }
    }
    window.addEventListener('popstate', onPop)
    return () => window.removeEventListener('popstate', onPop)
  }, [])

  // View navigation. Forward moves push a history entry so the browser/OS Back
  // button walks back through home → dashboard → editor as expected.
  const navigate = (next: 'home' | 'dashboard' | 'editor') => {
    if (view !== next) {
      try { window.history.pushState({ gv2View: next }, '') } catch { /* ignore */ }
    }
    setView(next)
  }
  const enterEditor = () => navigate('editor')
  const goDashboard = () => {
    setProjects(loadProjects())
    navigate('dashboard')
  }

  // Seed the editor state shared by every open path (deep-cloned so the preserved
  // example/registry is never mutated by edits). Pass null to seed NOTHING — used
  // when restoring a project from its own persistence key, so the host initialData
  // doesn't clobber the restored canvas (DiagramEditorMultiCanvas pushes initialData
  // into the active tab whenever it changes).
  const seedEditor = (data: DiagramData | null) => {
    const clone = data ? (JSON.parse(JSON.stringify(data)) as DiagramData) : null
    setDiagramData(clone)
    previousDataRef.current = clone
    editTrackerRef.current = createEditTracker()
    initialNodesRef.current = new Set(clone?.nodes.map((n) => n.id) ?? [])
    setImportCounter((c) => c + 1)
    resetTracking()
  }

  // Open a preserved showcase example as an EPHEMERAL canvas (explore/preview, not
  // saved). Drop the shared multi-canvas key so it opens fresh and fit-centered.
  const openExample = (ex: DemoExample) => {
    try { localStorage.removeItem('diagram-editor-multi-canvas') } catch { /* ignore */ }
    setActiveProjectId(null)
    setPersistenceKey(undefined)
    seedEditor(ex.data)
    enterEditor()
  }

  // Open one of the user's persistent projects. Its canvases restore from the
  // project's own localStorage key; the seed below is only used if that key is
  // empty (a brand-new project).
  const openProject = (project: Project) => {
    setActiveProjectId(project.id)
    setPersistenceKey(canvasKeyFor(project.id))
    // If the project already has saved canvases, restore them (seed nothing, so the
    // restored content isn't clobbered). Only seed a blank for a brand-new project.
    let hasSaved = false
    try {
      const raw = localStorage.getItem(canvasKeyFor(project.id))
      if (raw) {
        const st = JSON.parse(raw)
        hasSaved = Array.isArray(st.tabs) && st.tabs.length > 0
      }
    } catch { /* ignore */ }
    seedEditor(hasSaved ? null : blankOfType(project.diagramType))
    enterEditor()
  }

  // Create a fresh project and open it.
  const newProject = () => {
    const project = createProject('Untitled project', 'architecture')
    setProjects(loadProjects())
    openProject(project)
  }

  // In-app Back — go through history so it stays in sync with the Back button.
  const goBack = () => {
    setProjects(loadProjects())
    try { window.history.back() } catch { setView(collabParams ? 'editor' : 'home') }
  }
  const backToGallery = goBack

  const handleRenameProject = (id: string, name: string) => {
    renameProjectInStore(id, name)
    setProjects(loadProjects())
  }
  const handleDeleteProject = async (id: string) => {
    const ok = await modal.confirm({
      message: 'Delete this project? Its canvases will be permanently removed.',
      type: 'warning',
    })
    if (!ok) return
    deleteProjectInStore(id)
    setProjects(loadProjects())
  }

  // Toggle functions
  const toggleNodePalette = () => {
    setUIState(prev => ({ ...prev, showNodePalette: !prev.showNodePalette }))
  }

  const togglePropertiesPanel = () => {
    setUIState(prev => ({ ...prev, showPropertiesPanel: !prev.showPropertiesPanel }))
  }

  const toggleGrid = () => {
    setShowGrid(prev => !prev)
  }

  // File Menu Actions
  const handleNewDiagram = async () => {
    // Check for unsaved changes before opening modal
    if (hasUnsavedChanges) {
      const proceed = await warnBeforeLeaving(
        'You have unsaved changes. Creating a new diagram will discard them. Continue?'
      )
      if (!proceed) return
    }

    setShowNewDiagramModal(true)
  }

  const handleCreateDiagram = (config: NewDiagramConfig) => {
    const newDiagram: DiagramData = {
      diagramType: config.diagramType as any,
      title: config.title,
      description: config.description,
      nodes: [],
      edges: [],
      containers: [],
      layout: {
        algorithm: 'dagre',
        direction: config.diagramType === 'architecture' ? 'LR' : 'TB',
      },
    }

    // Create new tab with the diagram data
    if (addTabRef.current) {
      addTabRef.current(config.title, newDiagram)
    } else {
      // Fallback: update current diagram data
      setDiagramData(newDiagram)
    }

    setShowNewDiagramModal(false)

    // Reset edit tracking
    editTrackerRef.current = createEditTracker()
    initialNodesRef.current = new Set()
    previousDataRef.current = newDiagram

    // Mark as saved (fresh diagram, no changes yet)
    resetTracking()
  }

  const handleOpenJSON = async () => {
    console.log('🟠 [App] handleOpenJSON called - button clicked!')

    // Check for unsaved changes before opening modal
    if (hasUnsavedChanges) {
      console.log('🟠 [App] Has unsaved changes, showing confirm dialog')
      const proceed = await warnBeforeLeaving(
        'You have unsaved changes. Opening a new diagram will discard them. Continue?'
      )
      console.log('🟠 [App] User response:', proceed)
      if (!proceed) {
        console.log('🟠 [App] User cancelled, not opening modal')
        return
      }
    }

    console.log('🟠 [App] Opening JSON import modal...')
    setIsJSONImportModalOpen(true)
    console.log('🟠 [App] setIsJSONImportModalOpen(true) called')
  }

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    // Check for unsaved changes
    if (hasUnsavedChanges) {
      const proceed = await warnBeforeLeaving(
        'You have unsaved changes. Opening a new file will discard them. Continue?'
      )
      if (!proceed) {
        event.target.value = ''
        return
      }
    }

    const reader = new FileReader()
    reader.onload = (e) => {
      try {
        const content = e.target?.result as string
        const parsed = importFromJSON(content)
        setDiagramData(parsed)
        setError('')

        // Reset edit tracking
        editTrackerRef.current = createEditTracker()
        initialNodesRef.current = new Set(parsed.nodes.map(n => n.id))
        previousDataRef.current = parsed

        // Mark as saved after loading
        resetTracking()
      } catch (err) {
        setError(`Failed to parse JSON: ${err}`)
        modal.alert({ message: `Failed to parse JSON: ${err}`, type: 'error' })
      }
    }
    reader.readAsText(file)

    // Reset input
    event.target.value = ''
  }

  const handleSaveJSON = () => {
    if (!diagramData) return
    const json = JSON.stringify(diagramData, null, 2)
    const blob = new Blob([json], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `${diagramData.title || 'diagram'}.json`
    link.click()
    URL.revokeObjectURL(url)

    // Mark as saved after export
    markAsSaved()
  }

  const handleImportFromModal = (data: DiagramData) => {
    console.log('🟢 [App] handleImportFromModal called')
    console.log('🟢 [App] Received data:', data)
    console.log('🟢 [App] Data structure:', {
      nodes: data.nodes?.length,
      edges: data.edges?.length,
      containers: data.containers?.length,
      title: data.title,
      diagramType: data.diagramType
    })

    setDiagramData(data)
    console.log('🟢 [App] setDiagramData called')
    setError('')

    // Force reload by incrementing counter
    setImportCounter(prev => prev + 1)
    console.log('🟢 [App] Import counter incremented to force reload')

    // Reset edit tracking
    editTrackerRef.current = createEditTracker()
    initialNodesRef.current = new Set(data.nodes.map(n => n.id))
    previousDataRef.current = data

    // Mark as saved after loading
    resetTracking()

    console.log('✅ [App] Import completed successfully')
  }

  const handleExportFromModal = (options: ExportOptions) => {
    if (!diagramData) return

    console.log('🔍 Export options:', options)
    console.log('📊 Edit tracker:', editTrackerRef.current)

    if (options.format === 'json') {
      const exportMode = options.exportMode || 'full'
      let json: string

      if (exportMode === 'ai') {
        // Export AI-friendly format with edit tracking
        json = exportForAI(diagramData, [], [], editTrackerRef.current, {
          prettify: true,
          includeEditMetadata: true  // Include _userEdits metadata
        })
      } else {
        // Export full expanded format
        json = exportFull(diagramData, [], [], {
          prettify: true,
          includeOriginal: false
        })
      }

      console.log('📄 Exported JSON preview:', json.substring(0, 500))

      if (options.jsonAction === 'copy') {
        // Copy to clipboard
        console.log('📋 Copying to clipboard...')
        navigator.clipboard.writeText(json)
        modal.alert({
          message: `${exportMode === 'ai' ? 'AI-Friendly' : 'Full'} JSON copied to clipboard!`,
          type: 'success'
        })
      } else {
        // Download
        console.log('💾 Downloading file...')
        const blob = new Blob([json], { type: 'application/json' })
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        const suffix = exportMode === 'ai' ? '-ai' : ''
        link.download = `${diagramData.title || 'diagram'}${suffix}.json`
        link.href = url
        link.click()
        URL.revokeObjectURL(url)
      }

      markAsSaved()
    } else {
      // Handle PNG/SVG export (to be implemented)
      console.log('Export as', options.format, 'not yet implemented')
    }
  }

  const handleExport = (json: string) => {
    // Copy to clipboard
    navigator.clipboard.writeText(json)
    modal.alert({ message: 'JSON copied to clipboard!', type: 'success' })
    // Mark as saved after export
    markAsSaved()
  }

  const handleChange = (data: DiagramData) => {
    // This is called whenever the diagram is edited
    const previousData = previousDataRef.current

    if (previousData && data) {
      // Track node additions
      const previousNodeIds = new Set(previousData.nodes.map(n => n.id))
      const currentNodeIds = new Set(data.nodes.map(n => n.id))

      data.nodes.forEach(node => {
        if (!previousNodeIds.has(node.id) && !initialNodesRef.current.has(node.id)) {
          console.log('➕ Tracking node add:', node.id)
          trackNodeAdd(editTrackerRef.current, node.id)
        }
      })

      // Track node deletions
      previousData.nodes.forEach(node => {
        if (!currentNodeIds.has(node.id)) {
          console.log('➖ Tracking node delete:', node.id)
          trackNodeDelete(editTrackerRef.current, node.id)
        }
      })

      // Track node moves (position changes)
      data.nodes.forEach(node => {
        const prevNode = previousData.nodes.find(n => n.id === node.id)
        if (prevNode &&
            (prevNode.position?.x !== node.position?.x ||
             prevNode.position?.y !== node.position?.y)) {
          console.log('🔄 Tracking node move:', node.id)
          trackNodeMove(editTrackerRef.current, node.id)
        }
      })

      // Track style changes
      data.nodes.forEach(node => {
        const prevNode = previousData.nodes.find(n => n.id === node.id)
        if (prevNode && JSON.stringify(prevNode.style) !== JSON.stringify(node.style)) {
          console.log('🎨 Tracking node style:', node.id)
          trackNodeStyle(editTrackerRef.current, node.id)
        }
      })
    }

    previousDataRef.current = data
    setDiagramData(data) // Update state to reflect changes
    // Mark as unsaved when diagram changes
    markAsUnsaved()
    // Persist project metadata (the canvas content auto-persists via the editor's
    // per-project key); keep the registry's type + updatedAt fresh for the home grid.
    if (activeProjectId) {
      touchProject(activeProjectId, { diagramType: data.diagramType })
    }
  }

  const handleClear = async () => {
    // Clear only nodes, edges, and containers - preserve metadata
    if (!diagramData) return

    // Check for unsaved changes
    if (hasUnsavedChanges) {
      const proceed = await warnBeforeLeaving(
        'You have unsaved changes. Clearing the diagram will discard them. Continue?'
      )
      if (!proceed) return
    }

    modal.confirm({
      message: 'Clear all nodes and edges? (Title and diagram type will be preserved)',
      type: 'warning'
    }).then((confirmed) => {
      if (confirmed) {
        const clearedData = {
          ...diagramData,
          nodes: [],
          edges: [],
          containers: [],
        }
        setDiagramData(clearedData)

        // Reset edit tracking
        editTrackerRef.current = createEditTracker()
        initialNodesRef.current = new Set()
        previousDataRef.current = clearedData

        // Reset unsaved changes after clearing
        resetTracking()
      }
    })
  }

  // Edit Menu Actions
  const handleCopy = () => {
    // Copy is handled by DiagramEditorEnhanced keyboard shortcuts (Ctrl+C)
    modal.alert({
      title: 'Copy Tip',
      message: 'Select nodes and press Ctrl+C to copy, or use the right-click menu!',
      type: 'info'
    })
  }

  const handlePaste = () => {
    // Paste is handled by DiagramEditorEnhanced keyboard shortcuts (Ctrl+V)
    modal.alert({
      title: 'Paste Tip',
      message: 'Press Ctrl+V to paste copied nodes, or use the right-click menu!',
      type: 'info'
    })
  }

  const handleCut = () => {
    // Cut is handled by DiagramEditorEnhanced keyboard shortcuts (Ctrl+X)
    modal.alert({
      title: 'Cut Tip',
      message: 'Select nodes and press Ctrl+X to cut (Copy + Delete)!',
      type: 'info'
    })
  }

  const handleSelectAll = () => {
    console.log('Select All - to be implemented')
    // TODO: Implement select all
  }

  const handleDeleteSelected = () => {
    // Delete is handled by DiagramEditorEnhanced keyboard shortcuts (Delete/Backspace)
    modal.alert({
      title: 'Delete Tip',
      message: 'Select nodes and press Delete or Backspace to remove them!',
      type: 'info'
    })
  }

  // View Menu Actions
  const handleFitView = () => {
    console.log('Fit View - handled by ReactFlow Controls')
  }

  const handleZoomIn = () => {
    console.log('Zoom In - handled by ReactFlow Controls')
  }

  const handleZoomOut = () => {
    console.log('Zoom Out - handled by ReactFlow Controls')
  }

  // Layout Menu Actions (placeholders)
  const handleAutoLayout = (type: string) => {
    console.log(`Auto Layout: ${type}`)
  }

  const handleAlign = (direction: string) => {
    console.log(`Align: ${direction}`)
  }

  const handleDistribute = (direction: string) => {
    console.log(`Distribute: ${direction}`)
  }

  // Help Menu Actions
  const handleShowDocumentation = () => {
    window.open('https://github.com/your-repo/docs', '_blank')
  }

  const handleShowKeyboardShortcuts = () => {
    modal.alert({
      title: 'Keyboard Shortcuts',
      message: `Ctrl+N - New Diagram
Ctrl+O - Open JSON
Ctrl+S - Save JSON
Ctrl+Z - Undo
Ctrl+Y - Redo
Ctrl+L - Toggle Node Library
Ctrl+P - Toggle Properties Panel
Ctrl+G - Toggle Grid
Delete - Delete Selected`,
      type: 'info'
    })
  }

  const handleShowAbout = () => {
    modal.alert({
      title: 'About',
      message: 'Diagram Editor v1.0\n\nA professional diagram editor for creating architecture diagrams, flowcharts, and more.',
      type: 'info'
    })
  }

  // Keyboard Shortcuts
  useKeyboardShortcuts([
    { ...CommonShortcuts.NEW, action: handleNewDiagram },
    { ...CommonShortcuts.OPEN, action: handleOpenJSON },
    { ...CommonShortcuts.SAVE, action: handleSaveJSON },
    { ...CommonShortcuts.TOGGLE_NODE_LIBRARY, action: toggleNodePalette },
    { ...CommonShortcuts.TOGGLE_PROPERTIES, action: togglePropertiesPanel },
    { ...CommonShortcuts.TOGGLE_GRID, action: toggleGrid },
    { ...CommonShortcuts.HELP, action: handleShowDocumentation },
    { ...CommonShortcuts.SHORTCUTS, action: handleShowKeyboardShortcuts },
  ])

  // Marketing landing (skipped when joining a collab room).
  if (view === 'home') {
    return (
      <DemoGallery
        examples={DEMO_EXAMPLES}
        onOpen={openExample}
        onOpenDashboard={goDashboard}
      />
    )
  }

  // Projects dashboard ("your files").
  if (view === 'dashboard') {
    return (
      <Dashboard
        projects={projects}
        onOpenProject={openProject}
        onNewProject={newProject}
        onRenameProject={handleRenameProject}
        onDeleteProject={handleDeleteProject}
        onHome={goBack}
        onExploreShowcase={goBack}
      />
    )
  }

  return (
    <div className="app">
      {/* Hidden file input for Open JSON */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".json"
        style={{ display: 'none' }}
        onChange={handleFileUpload}
      />

      {/* New Diagram Modal */}
      <NewDiagramModal
        isOpen={showNewDiagramModal}
        onClose={() => setShowNewDiagramModal(false)}
        onCreate={handleCreateDiagram}
      />

      {/* Export Options Modal */}
      <ExportOptionsModal
        isOpen={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        onExport={handleExportFromModal}
        defaultFormat="json"
      />

      {/* JSON Import Modal */}
      <JSONImportModal
        isOpen={isJSONImportModalOpen}
        onClose={() => setIsJSONImportModalOpen(false)}
        onImport={handleImportFromModal}
      />

      {/* Header */}
      <div className="header">
        <div className="brand">
          {!collabParams && (
            <button className="brand-back" onClick={backToGallery} title="Back to home" aria-label="Back to home">
              ←
            </button>
          )}
          <span className="brand-mark" aria-hidden>◳</span>
          <span className="brand-name">Graphing<span className="brand-accent">V2</span></span>
          {collabParams && <span className="brand-live">● live</span>}
        </div>
        <div className="header-actions">
          <button onClick={() => setShowJsonEditor(!showJsonEditor)}>
            {showJsonEditor ? 'Hide JSON' : 'JSON'}
          </button>
          <button onClick={() => setDiagramData(exampleDiagram)}>
            Load Example
          </button>
          <button onClick={handleClear}>Clear</button>
        </div>
      </div>

      {/* Menu Bar */}
      <MenuBar
          // File menu
          onNewDiagram={handleNewDiagram}
          onOpenJSON={handleOpenJSON}
          onSaveJSON={handleSaveJSON}
          onExport={() => setIsExportModalOpen(true)}
          // Edit menu
          onUndo={() => console.log('Undo - handled by editor')}
          onRedo={() => console.log('Redo - handled by editor')}
          onCopy={handleCopy}
          onPaste={handlePaste}
          onCut={handleCut}
          onSelectAll={handleSelectAll}
          onDeleteSelected={handleDeleteSelected}
          canUndo={false}
          canRedo={false}
          hasSelection={false}
          // View menu
          onToggleNodeLibrary={toggleNodePalette}
          onTogglePropertiesPanel={togglePropertiesPanel}
          onToggleGrid={toggleGrid}
          onFitView={handleFitView}
          onZoomIn={handleZoomIn}
          onZoomOut={handleZoomOut}
          showNodeLibrary={uiState.showNodePalette}
          showPropertiesPanel={uiState.showPropertiesPanel}
          showGrid={showGrid}
          // Layout menu
          onAutoLayoutHierarchical={() => handleAutoLayout('hierarchical')}
          onAutoLayoutForce={() => handleAutoLayout('force')}
          onAutoLayoutGrid={() => handleAutoLayout('grid')}
          onAutoLayoutCircular={() => handleAutoLayout('circular')}
          onAlignLeft={() => handleAlign('left')}
          onAlignRight={() => handleAlign('right')}
          onAlignTop={() => handleAlign('top')}
          onAlignBottom={() => handleAlign('bottom')}
          onDistributeHorizontally={() => handleDistribute('horizontal')}
          onDistributeVertically={() => handleDistribute('vertical')}
          // Help menu
          onShowDocumentation={handleShowDocumentation}
          onShowKeyboardShortcuts={handleShowKeyboardShortcuts}
          onShowAbout={handleShowAbout}
        />

      <div className="content">
        <>
          {/* JSON Import - Enhanced inline editor */}
            {showJsonEditor && (
              <JSONImportSidebar
                onImport={handleImportFromModal}
                error={error}
                setError={setError}
              />
            )}

            {/* Diagram Editor with Multi-Canvas */}
            <div className="editor-container">
              <DiagramEditorMultiCanvas
                key={`diagram-editor-${activeProjectId ?? 'ephemeral'}-${importCounter}`}
                initialData={diagramData ?? undefined}
                enablePersistence={true}
                persistenceKey={persistenceKey}
                onChange={handleChange}
                onExport={handleExport}
                onCanvasAdd={(addTabFn) => {
                  addTabRef.current = addTabFn
                }}
                readonly={false}
                showToolbar={true}
                showMinimap={true}
                showControls={true}
                showPropertiesPanel={uiState.showPropertiesPanel}
                showNodePalette={uiState.showNodePalette}
                onTogglePropertiesPanel={togglePropertiesPanel}
                onToggleNodePalette={toggleNodePalette}
                autoLayout={true}
                theme="light"
                showGrid={showGrid}
                {...(collabParams ? { collab: collabParams } : {})}
              />
            </div>
          </>
      </div>
    </div>
  )
}

// Wrap with ModalProvider
export default function App() {
  return (
    <ModalProvider>
      <AppInner />
    </ModalProvider>
  )
}
